import React, { useState } from 'react';
import { Typography, Card, Upload, Button, Alert, List, Divider, Space } from 'antd';
import { InboxOutlined, UploadOutlined, FileExcelOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

export default function DsmMinIngestionPage() {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorDetails, setErrorDetails] = useState([]);

  const handleUpload = async () => {
    if (fileList.length === 0) return;
    
    const formData = new FormData();
    formData.append('file', fileList[0]);

    setUploading(true);
    setResult(null);
    setErrorDetails([]);

    try {
      const res = await api.post('/dsm/ingestion/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult({
        success: true,
        message: res.data.message || 'Excel file ingested successfully.'
      });
      setFileList([]);
    } catch (err) {
      console.error(err);
      const resData = err.response?.data || {};
      setResult({
        success: false,
        message: resData.error || 'Failed to upload and ingest file.'
      });
      if (resData.details) {
        setErrorDetails(resData.details);
      }
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      setFileList([file]);
      return false; // Prevent automatic upload
    },
    fileList,
    maxCount: 1,
    accept: '.xlsx, .xls, .csv'
  };

  return (
    <div style={{ padding: '32px 40px', maxWidth: 900, margin: '0 auto' }}>
      {/* Title block */}
      <div style={{ marginBottom: 32 }}>
        <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>
          Data Ingestion
        </Title>
        <Paragraph style={{ color: '#64748b', marginTop: 8, fontSize: 14 }}>
          Upload historical wind forecasting and provisional generation Excel or CSV files to load raw values into the <strong>DSM Minimization</strong> database.
        </Paragraph>
      </div>

      <Card style={{ borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)' }}>
        <Dragger {...uploadProps} style={{ padding: 24, borderRadius: 12, background: '#f8fafc' }}>
          <p className="ant-upload-drag-icon" style={{ marginBottom: 16 }}>
            <InboxOutlined style={{ color: '#0052CC', fontSize: 48 }} />
          </p>
          <p className="ant-upload-text" style={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>
            Drag and drop wind energy Excel or CSV files here, or click to browse
          </p>
          <p className="ant-upload-hint" style={{ color: '#64748b', fontSize: 13 }}>
            Supported formats: .xlsx, .xls, .csv. Make sure columns like Date, Time Block, Forecasted Schedule (MAL), AVC, and SEMS Provisional (Actual Generation) are present.
          </p>
        </Dragger>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleUpload}
            disabled={fileList.length === 0}
            loading={uploading}
            style={{
              height: 40,
              padding: '0 24px',
              fontWeight: 500,
              borderRadius: 8,
              background: '#0052CC',
              borderColor: '#0052CC'
            }}
          >
            Start Ingestion
          </Button>
        </div>
      </Card>

      {/* Upload Results */}
      {result && (
        <div style={{ marginTop: 32 }}>
          {result.success ? (
            <Alert
              message={
                <Space>
                  <CheckCircleOutlined style={{ color: '#27ae60' }} />
                  <strong style={{ fontSize: 15 }}>Ingestion Successful</strong>
                </Space>
              }
              description={result.message}
              type="success"
              showIcon={false}
              style={{ borderRadius: 12, padding: 16, border: '1px solid #d4edda' }}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Alert
                message={
                  <Space>
                    <CloseCircleOutlined style={{ color: '#c0392b' }} />
                    <strong style={{ fontSize: 15 }}>Ingestion Failed</strong>
                  </Space>
                }
                description={result.message}
                type="error"
                showIcon={false}
                style={{ borderRadius: 12, padding: 16, border: '1px solid #f8d7da' }}
              />
              
              {errorDetails.length > 0 && (
                <Card
                  title={<span style={{ color: '#c0392b', fontWeight: 600 }}>Validation Error Log</span>}
                  style={{ borderRadius: 12, border: '1px solid #f5c6cb' }}
                  styles={{ body: { maxHeight: 300, overflowY: 'auto', padding: '12px 24px' } }}
                >
                  <List
                    size="small"
                    dataSource={errorDetails}
                    renderItem={(item) => (
                      <List.Item style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <Text type="danger" style={{ fontSize: 13, fontFamily: 'monospace' }}>{item}</Text>
                      </List.Item>
                    )}
                  />
                </Card>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
